import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { fetchCagriIstatistik } from '../services/santralYonetimService';
import './CagriMerkeziDashboard.css';

const RENK = {
  gelen: '#2563eb',
  cevaplandi: '#16a34a',
  cevapsiz: '#f59e0b',
  terk: '#ef4444',
  giden: '#8b5cf6',
};

const OTO_YENILE_MS = 45000;

const buAy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const sayi = (n) => Number(n || 0).toLocaleString('tr-TR');
const yuzde = (parca, toplam) => (toplam > 0 ? Math.round((parca / toplam) * 100) : 0);

const sureKisa = (sn) => {
  const s = Number(sn) || 0;
  if (s <= 0) return '0 dk';
  const saat = Math.floor(s / 3600);
  const dk = Math.floor((s % 3600) / 60);
  if (saat > 0) return `${saat}s ${dk}dk`;
  return `${dk} dk`;
};

const ayEtiketi = (anahtar) => {
  const m = String(anahtar || '').match(/^(\d{4})-(\d{2})$/);
  if (!m) return '';
  const aylar = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  return `${aylar[Number(m[2]) - 1]} ${m[1]}`;
};

/** Recharts tooltip ortak stili */
const tooltipStyle = {
  borderRadius: 10,
  border: '1px solid #e2e8f0',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
  fontSize: 12,
};

const KpiKart = ({ ikon, etiket, deger, alt, tonu, vurgu }) => (
  <div className={`cm-kpi${vurgu ? ' cm-kpi--vurgu' : ''}`} data-ton={tonu}>
    <div className="cm-kpi-ikon">
      <i className={`fas fa-${ikon}`} aria-hidden />
    </div>
    <div className="cm-kpi-govde">
      <span className="cm-kpi-deger">{deger}</span>
      <span className="cm-kpi-etiket">{etiket}</span>
      {alt ? <span className="cm-kpi-alt">{alt}</span> : null}
    </div>
  </div>
);

const CagriMerkeziDashboard = () => {
  const [ay, setAy] = useState(buAy());
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [sonGuncelleme, setSonGuncelleme] = useState(null);
  const ilkYuklemeRef = useRef(true);

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setYukleniyor(true);
    setHata('');
    try {
      const r = await fetchCagriIstatistik(ay ? { ay } : {});
      if (r?.basarili) {
        setVeri(r.veri);
        setSonGuncelleme(new Date());
      } else {
        setHata(r?.mesaj || 'İstatistikler alınamadı');
      }
    } catch (err) {
      setHata(err?.message || 'İstatistikler alınamadı');
    } finally {
      setYukleniyor(false);
      ilkYuklemeRef.current = false;
    }
  }, [ay]);

  useEffect(() => {
    yukle();
  }, [yukle]);

  useEffect(() => {
    const t = setInterval(() => yukle(true), OTO_YENILE_MS);
    return () => clearInterval(t);
  }, [yukle]);

  const bugun = veri?.bugun || {};
  const onceki = veri?.oncekiMesaiDisi || {};
  const aylikOzet = veri?.aylikOzet || {};

  const pieData = useMemo(() => ([
    { ad: 'Cevaplandı', deger: bugun.cevaplandi || 0, renk: RENK.cevaplandi },
    { ad: 'Cevapsız', deger: bugun.cevapsiz || 0, renk: RENK.cevapsiz },
    { ad: 'Terk', deger: bugun.terk || 0, renk: RENK.terk },
  ]), [bugun.cevaplandi, bugun.cevapsiz, bugun.terk]);
  const pieToplam = pieData.reduce((a, b) => a + b.deger, 0);

  const aylik = veri?.aylik || [];
  const saatlik = (veri?.saatlikBugun || []).map((s) => ({ ...s, etiket: `${String(s.saat).padStart(2, '0')}:00` }));

  const oncekiToplam = (onceki.cevapsiz || 0) + (onceki.terk || 0);

  return (
    <div className="cm-dashboard">
      <div className="cm-toolbar">
        <div className="cm-toolbar-info">
          {sonGuncelleme ? (
            <span className="cm-updated">
              <i className="fas fa-circle cm-live-dot" aria-hidden /> Canlı · {sonGuncelleme.toLocaleTimeString('tr-TR')}
            </span>
          ) : null}
        </div>
        <div className="cm-toolbar-actions">
          <label className="cm-month-field">
            <i className="fas fa-calendar-alt" aria-hidden />
            <input
              type="month"
              value={ay}
              max={buAy()}
              onChange={(e) => setAy(e.target.value || buAy())}
            />
          </label>
          <button type="button" className="cm-refresh" onClick={() => yukle()} disabled={yukleniyor}>
            <i className={`fas fa-sync-alt${yukleniyor ? ' fa-spin' : ''}`} aria-hidden /> Yenile
          </button>
        </div>
      </div>

      {hata ? (
        <div className="cm-error">
          <i className="fas fa-exclamation-circle" aria-hidden /> {hata}
        </div>
      ) : null}

      {yukleniyor && ilkYuklemeRef.current ? (
        <div className="cm-loading">
          <i className="fas fa-spinner fa-spin" aria-hidden /> İstatistikler yükleniyor…
        </div>
      ) : (
        <>
          {/* Bugünkü mesai KPI'ları */}
          <div className="cm-section-label">
            <i className="fas fa-business-time" aria-hidden /> Bugünkü mesai
          </div>
          <div className="cm-kpi-grid">
            <KpiKart ikon="phone-volume" etiket="Gelen çağrı" deger={sayi(bugun.gelen)} tonu="gelen" />
            <KpiKart
              ikon="phone"
              etiket="Cevaplandı"
              deger={sayi(bugun.cevaplandi)}
              alt={`%${yuzde(bugun.cevaplandi, bugun.gelen)} cevaplama`}
              tonu="cevaplandi"
            />
            <KpiKart
              ikon="phone-slash"
              etiket="Cevapsız"
              deger={sayi(bugun.cevapsiz)}
              alt={`%${yuzde(bugun.cevapsiz, bugun.gelen)}`}
              tonu="cevapsiz"
            />
            <KpiKart
              ikon="ban"
              etiket="Terk (iptal)"
              deger={sayi(bugun.terk)}
              alt={`%${yuzde(bugun.terk, bugun.gelen)}`}
              tonu="terk"
            />
            <KpiKart
              ikon="moon"
              etiket="Önceki mesai dışı cevapsız"
              deger={sayi(oncekiToplam)}
              alt={`${sayi(onceki.cevapsiz)} cevapsız · ${sayi(onceki.terk)} terk`}
              tonu="onceki"
              vurgu
            />
          </div>

          {/* Bugün dağılım + saatlik */}
          <div className="cm-grid-2">
            <div className="cm-card">
              <div className="cm-card-head">
                <h3><i className="fas fa-chart-pie" aria-hidden /> Bugün durum dağılımı</h3>
                <span className="cm-card-sub">{sayi(pieToplam)} çağrı</span>
              </div>
              {pieToplam > 0 ? (
                <div className="cm-chart cm-chart--pie">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="deger"
                        nameKey="ad"
                        innerRadius="58%"
                        outerRadius="82%"
                        paddingAngle={2}
                        stroke="none"
                      >
                        {pieData.map((d) => (
                          <Cell key={d.ad} fill={d.renk} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [sayi(v), n]} />
                      <Legend verticalAlign="bottom" height={28} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="cm-pie-center">
                    <span className="cm-pie-center-deger">%{yuzde(bugun.cevaplandi, pieToplam)}</span>
                    <span className="cm-pie-center-etiket">cevaplandı</span>
                  </div>
                </div>
              ) : (
                <div className="cm-empty"><i className="fas fa-inbox" aria-hidden /> Bugün henüz çağrı yok</div>
              )}
            </div>

            <div className="cm-card">
              <div className="cm-card-head">
                <h3><i className="fas fa-clock" aria-hidden /> Saatlik dağılım (bugün)</h3>
              </div>
              <div className="cm-chart cm-chart--bar">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={saatlik} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                    <XAxis dataKey="etiket" tick={{ fontSize: 10, fill: '#94a3b8' }} interval={2} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: 'rgba(37,99,235,0.06)' }} formatter={(v, n) => [sayi(v), n === 'gelen' ? 'Gelen' : n]} />
                    <Bar dataKey="gelen" name="Gelen" fill={RENK.gelen} radius={[4, 4, 0, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Aylık trend */}
          <div className="cm-card">
            <div className="cm-card-head">
              <h3><i className="fas fa-chart-line" aria-hidden /> Aylık trend — {ayEtiketi(veri?.secili?.anahtar)}</h3>
              <div className="cm-month-summary">
                <span className="cm-ms cm-ms--gelen">{sayi(aylikOzet.gelen)} gelen</span>
                <span className="cm-ms cm-ms--cevaplandi">{sayi(aylikOzet.cevaplandi)} cevaplandı</span>
                <span className="cm-ms cm-ms--cevapsiz">{sayi(aylikOzet.cevapsiz)} cevapsız</span>
                <span className="cm-ms cm-ms--terk">{sayi(aylikOzet.terk)} terk</span>
                <span className="cm-ms cm-ms--sure">{sureKisa(aylikOzet.toplamSureSn)} konuşma</span>
              </div>
            </div>
            <div className="cm-chart cm-chart--area">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={aylik} margin={{ top: 10, right: 12, left: -16, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cmGelen" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={RENK.gelen} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={RENK.gelen} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                  <XAxis dataKey="gun" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    labelFormatter={(g) => `${g}. gün`}
                    formatter={(v, n) => {
                      const adlar = { gelen: 'Gelen', cevaplandi: 'Cevaplandı', cevapsiz: 'Cevapsız', terk: 'Terk' };
                      return [sayi(v), adlar[n] || n];
                    }}
                  />
                  <Legend verticalAlign="top" height={28} iconType="circle" formatter={(n) => ({ gelen: 'Gelen', cevaplandi: 'Cevaplandı', cevapsiz: 'Cevapsız', terk: 'Terk' }[n] || n)} />
                  <Area type="monotone" dataKey="gelen" stroke={RENK.gelen} strokeWidth={2} fill="url(#cmGelen)" />
                  <Area type="monotone" dataKey="cevaplandi" stroke={RENK.cevaplandi} strokeWidth={2} fillOpacity={0} />
                  <Area type="monotone" dataKey="cevapsiz" stroke={RENK.cevapsiz} strokeWidth={2} fillOpacity={0} />
                  <Area type="monotone" dataKey="terk" stroke={RENK.terk} strokeWidth={2} fillOpacity={0} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CagriMerkeziDashboard;
